import { useEffect, useRef, useState } from 'react'
import { ThemeToggle } from './ThemeToggle'
import { NotificationBell } from './NotificationBell'
import { useMutation, useQuery } from '@tanstack/react-query'
import { reopenWelcome } from '../lib/guest'
import { IS_USTOOL } from '../lib/appKind'
import { api } from '../lib/api'
import { hasSession } from '../lib/session'
import { useI18n } from '../lib/i18n'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Brand } from './Brand'
import { useT } from '../lib/i18n'

/**
 * The mobile primitives.
 *
 * Deliberately not a port of the desktop ui.tsx. The shapes differ because
 * the interactions differ: a screen owns its own scroller, a dialog is a
 * sheet that comes up from the thumb rather than a box in the middle of
 * nowhere, and a back action is a chevron in the top-left because that is
 * where a phone puts it.
 */

/* ------------------------------------------------------------------ icons */

/**
 * Inline SVG, not an icon font or a package.
 *
 * A tab bar with six glyphs does not justify a dependency, and an icon font
 * makes the first paint wait on a network round trip -- on a phone, on
 * mobile data, that is the difference between instant and not.
 */
/**
 * The glyphs that mean "the way you are reading", and so have to turn around
 * in Urdu and Arabic. The row they sit in already reverses; the arrow inside
 * it did not, so on the date stepper the chevron that LOOKED like "next day"
 * was the one wired to "previous day" -- and on the earliest date that button
 * is disabled, which is why pressing it did nothing at all.
 *
 * `signout` is deliberately absent: its arrow means "out through that door",
 * not "forward", and the door is drawn on the left of the glyph.
 */
const MIRRORED = new Set<IconName>(['back', 'chevron'])

export function Icon({ name, className = '' }: { name: IconName; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         className={`${className || 'h-6 w-6'}${MIRRORED.has(name) ? ' rtl:-scale-x-100' : ''}`}>
      {PATHS[name]}
    </svg>
  )
}

export type IconName =
  | 'home' | 'send' | 'seat' | 'ticket' | 'person'
  | 'back' | 'close' | 'search' | 'chevron' | 'check' | 'plus' | 'minus'
  | 'pin' | 'clock' | 'alert' | 'globe' | 'signout' | 'mic' | 'box'
  | 'upload' | 'camera' | 'bell'
  /* 0063's comfort vocabulary. Drawn here rather than imported, like every
     other glyph in this file, so they inherit the theme's accent. */
  | 'door' | 'ac' | 'wifi' | 'usb' | 'water' | 'recliner' | 'curtains'
  | 'wheelchair' | 'childSeat'
  /* 0064's eight. Four of them -- the Zamzam cooler, the prayer stop, the
     ladies section, the washroom -- are what a pilgrim asks first and what
     no competitor app has a field for, so they needed glyphs of their own
     rather than a shared "tick" standing in for all four. */
  | 'socket' | 'zamzam' | 'washroom' | 'meal' | 'screen' | 'luggage'
  | 'prayerStop' | 'ladies'
  /* The two controls M4 adds to the departure list. */
  | 'sort' | 'filter'

const PATHS: Record<IconName, ReactNode> = {
  home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" /></>,
  send: <><rect x="2.5" y="7" width="13" height="10" rx="2" /><path d="M15.5 10h3l3 3v4h-6z" /><circle cx="7" cy="18.5" r="1.8" /><circle cx="17.5" cy="18.5" r="1.8" /></>,
  seat: <><path d="M7 4v8a3 3 0 0 0 3 3h4" /><path d="M17 15v5" /><path d="M7 20h10" /><path d="M17 8v7" /></>,
  ticket: <><path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" /><path d="M13 6v12" strokeDasharray="2 2.5" /></>,
  person: <><circle cx="12" cy="8" r="3.5" /><path d="M4.5 20a7.5 7.5 0 0 1 15 0" /></>,
  // A parcel, for "send something" on the welcome screen. The van glyph is
  // `send`, which that screen uses for chartering a whole vehicle.
  box: <><path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5z" /><path d="M3.5 7.5 12 12l8.5-4.5" /><path d="M12 12v9" /></>,
  back: <path d="M15 5l-7 7 7 7" />,
  close: <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></>,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></>,
  chevron: <path d="M9 5l7 7-7 7" />,
  check: <path d="M4 12.5l5.5 5.5L20 7" />,
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus: <path d="M5 12h14" />,
  pin: <><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5.2l3 1.8" /></>,
  alert: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5" /><path d="M12 16.2v.2" /></>,
  mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" /><path d="M12 18v3" /></>,
  globe: <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17" /><path d="M12 3.5a13 13 0 0 1 0 17a13 13 0 0 1 0-17z" /></>,
  signout: <><path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2" /><path d="M10 12h11" /><path d="M18 9l3 3-3 3" /></>,
  // Both straight from the mockup's `.upl` labels -- the tray with the arrow
  // out of it, and the camera. Used nowhere else, which is why they arrive
  // with the vehicle form rather than earlier.
  upload: <><path d="M12 16V4" /><path d="M7 9l5-5 5 5" /><path d="M4 20h16" /></>,
  camera: <><path d="M3 8h4l2-3h6l2 3h4v11H3z" /><circle cx="12" cy="13" r="3.5" /></>,
  // The mockup's own bell, which the bar has wanted since it was drawn.
  bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  // What a vehicle is like inside. `pin` already serves live tracking.
  door: <><rect x="6" y="3" width="12" height="18" rx="1.5" /><circle cx="14" cy="12" r="1" /></>,
  ac: <path d="M12 3v18M4.6 7.5l14.8 8.6M19.4 7.5L4.6 16.1" />,
  wifi: <><path d="M4.5 10a11 11 0 0 1 15 0" /><path d="M8 13.6a6 6 0 0 1 8 0" /><path d="M12 17.3h.01" /></>,
  usb: <><path d="M12 21V5" /><circle cx="12" cy="3.6" r="1.4" /><path d="M12 15l4-3.2V9" /><rect x="14.6" y="6.6" width="2.8" height="2.4" rx="0.5" /><path d="M12 12L8.5 9.2" /><circle cx="8" cy="8.6" r="1.3" /></>,
  water: <path d="M12 3s5.8 6.1 5.8 9.8a5.8 5.8 0 0 1-11.6 0C6.2 9.1 12 3 12 3z" />,
  recliner: <><path d="M6 4v8.5a2 2 0 0 0 2 2h6.5" /><path d="M5 19h13a2 2 0 0 0 2-2v-3.5" /></>,
  curtains: <><path d="M3.5 4h17" /><path d="M7 4v16c2.2 0 3.3-2.2 3.3-5.4V4" /><path d="M17 4v16c-2.2 0-3.3-2.2-3.3-5.4V4" /></>,
  wheelchair: <><circle cx="11.2" cy="4.2" r="1.6" /><path d="M10 7.5V13h5l2.8 6" /><path d="M14.8 13.4A5.6 5.6 0 1 1 7.2 17.8" /></>,
  childSeat: <><path d="M7 20.5V9.5A4.5 4.5 0 0 1 11.5 5h1A4.5 4.5 0 0 1 17 9.5v11" /><path d="M7 14.5h10" /></>,
  // A wall socket with two pins -- the claim "you can plug a charger in",
  // which is not the claim USB charging makes.
  socket: <><rect x="3.5" y="5" width="17" height="14" rx="2.5" /><path d="M9.5 10v4M14.5 10v4" /></>,
  // A cooler with a tap: the Zamzam urn as it actually stands on a coach.
  zamzam: <><path d="M7 7h10l-1 13H8z" /><path d="M9 3.5h6v3.5H9z" /><path d="M16.5 12.5H19v2.5" /></>,
  // A door with the universal sign on it, because "washroom" is the word
  // people search for and a tap glyph reads as a sink.
  washroom: <><rect x="3.5" y="3.5" width="17" height="17" rx="2.5" /><circle cx="9" cy="9" r="1.4" /><path d="M9 11.5V16M7.2 13h3.6" /><circle cx="15.5" cy="9" r="1.4" /><path d="M14 16l1.5-4.5L17 16" /></>,
  meal: <><path d="M7 3v8a2 2 0 0 0 4 0V3" /><path d="M9 11v10" /><path d="M16 3c2 1.5 2.5 4 2 7-.3 1.8-1 2.5-2 2.5V21" /></>,
  screen: <><rect x="3" y="4.5" width="18" height="12" rx="2" /><path d="M9 20h6M12 16.5V20" /></>,
  luggage: <><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M9 8V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V8" /><path d="M12 11v6" /></>,
  // A prayer niche. Drawn as the arch, which is the one shape that reads as
  // prayer in every country this app ships in.
  prayerStop: <><path d="M12 3c3.3 0 6 2.7 6 6v12H6V9c0-3.3 2.7-6 6-6z" /><path d="M9.5 21v-5a2.5 2.5 0 0 1 5 0v5" /></>,
  ladies: <><circle cx="12" cy="6" r="2.6" /><path d="M12 8.6c-2.6 0-4 1.8-4 4.2 0 1 .4 1.6 1.2 1.8L8.5 21h7l-.7-6.4c.8-.2 1.2-.8 1.2-1.8 0-2.4-1.4-4.2-4-4.2z" /></>,
  sort: <><path d="M7 4v16" /><path d="M4 7l3-3 3 3" /><path d="M17 20V4" /><path d="M14 17l3 3 3-3" /></>,
  filter: <><path d="M4 6h16" /><path d="M7 12h10" /><path d="M10 18h4" /></>,
}

/* ----------------------------------------------------------------- screen */

/**
 * A screen: fixed header, one scrolling panel, nothing else scrolls.
 *
 * `onBack` turns the title bar into a pushed screen -- chevron on the left,
 * title centred, the way a phone signals depth. Without it the title is large
 * and left-aligned, the way a phone signals a root tab.
 *
 * `header` replaces that bar outright, for a screen whose top row is its own
 * control rather than a title. It still goes inside `.appbar`, which is what
 * carries the notch inset -- a screen that renders no header at all puts its
 * first row under the status bar.
 */
/**
 * A shield and one quiet line.
 *
 * The mockup closes several screens with one of these instead of another
 * control -- a reassurance where a page would otherwise just stop. Not a
 * banner and not an error: no fill, no border, ink-3.
 */
export function HintLine({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-ink-3">
      <svg viewBox="0 0 24 24" className="mt-0.5 h-[15px] w-[15px] shrink-0 text-accent" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round"
           strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3l7 3v5.5c0 4.3-3 8.1-7 9.5-4-1.4-7-5.2-7-9.5V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      <span className="text-[11px] leading-[1.6]">{children}</span>
    </div>
  )
}

/**
 * The three controls every app bar carries: language, notifications, theme.
 *
 * The owner, 2026-10-10: *"keep the globe button open on all pages of the
 * application"*. It was only in BookSwitch, which is the bar on Book now and
 * Send -- so on a ticket, an order, the business panes or the queue there was
 * no way to change language without going to Account.
 *
 * Defined once rather than repeated in Screen's two branches and BookSwitch,
 * because three copies of a bar drift and only one of them gets the next
 * control added to it.
 */
/**
 * Reopens the welcome sheet -- language and what you came for.
 *
 * It no longer changes country: country is set once at onboarding and decides
 * which cities exist, which is a settings-screen decision and still lives on
 * Account. What someone reaches for in a bar is the language.
 *
 * A storage clear plus a reload, because the sheet is decided above the
 * router before the shell renders. Account has done it this way since §58.
 */
export function LanguageGlobe({ label }: { label: string }) {
  const msg = useT()
  const { locale, setLocale } = useI18n()
  const [open, setOpen] = useState(false)

  const langs = useQuery<{ code: string; native_name: string }[]>({
    queryKey: ['languages'], queryFn: () => api('/languages'), enabled: open,
  })
  // Follows the person to another device, when there is an account to hang
  // it on. A guest's choice lives in this browser and that is enough.
  const save = useMutation({
    mutationFn: (code: string) => api('/me', { method: 'PATCH', body: { locale: code } }),
  })

  /*
   * IN USTOOL THE GLOBE OPENS A LANGUAGE PICKER, NOT THE WELCOME SCREEN.
   *
   * It used to call reopenWelcome() and reload -- which works in Qafila,
   * where the welcome asks language AND what you came for. Ustool never
   * renders that screen (its three intents are all passenger questions), so
   * clearing the flag changed nothing and the button did nothing at all.
   * That is what the owner hit.
   *
   * A carrier has no "what brings you here" to answer. He wants the
   * language, so the globe gives him exactly that, in place, without losing
   * the screen he was on.
   */
  if (IS_USTOOL) {
    return (
      <>
        <button
          type="button" onClick={() => setOpen(true)}
          aria-label={label} title={label}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border
                     border-accent/35 bg-accent-wash text-accent transition-colors
                     active:scale-95"
        >
          <Icon name="globe" className="h-[17px] w-[17px]" />
        </button>

        <Sheet open={open} onClose={() => setOpen(false)}
               title={msg('language.label')} full>
          <div className="grid grid-cols-2 gap-[7px]">
            {(langs.data ?? []).map((l) => (
              <button
                key={l.code} type="button" aria-pressed={locale === l.code}
                onClick={() => {
                  setLocale(l.code)
                  if (hasSession()) save.mutate(l.code)
                  setOpen(false)
                }}
                className={`min-h-[42px] rounded-lg border text-[12px] font-[650] ${
                  locale === l.code
                    ? 'border-accent bg-accent text-white'
                    : 'border-line bg-surface text-ink-2'}`}
              >{l.native_name}</button>
            ))}
          </div>
        </Sheet>
      </>
    )
  }

  // Qafila: the welcome screen, which asks language AND intent together.
  return (
    <button
      type="button"
      onClick={() => { reopenWelcome(); location.assign('/') }}
      aria-label={label}
      title={label}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full border
                 border-accent/35 bg-accent-wash text-accent transition-colors
                 active:scale-95"
    >
      <Icon name="globe" className="h-[17px] w-[17px]" />
    </button>
  )
}

export function BarControls() {
  const msg = useT()
  return (
    <>
      <LanguageGlobe label={msg('welcome.reopen')} />
      <NotificationBell />
      <ThemeToggle compact />
    </>
  )
}

export function Screen({
  title, onBack, action, header, children, large = false,
}: {
  title?: string
  onBack?: () => void
  action?: ReactNode
  /** A whole title bar of your own. Takes the place of title/onBack/action. */
  header?: ReactNode
  children: ReactNode
  /** Large left-aligned title, for a tab root. */
  large?: boolean
}) {
  const bar = title || onBack || action

  return (
    <div className="flex h-full flex-col">
      {/* Always rendered, even with nothing in it: `.appbar` is what carries
          the notch inset, and a screen without one would put its first row
          under the status bar. Empty, it collapses to that inset alone. */}
      <header className="appbar">
        {header}

        {/*
          THE PRODUCT'S OWN NAME, ON ITS OWN ROOT SCREENS.

          A screen with no bar of its own still gets the day/night switch --
          "keep the globe button open on all pages" -- and, since 2026-10-12,
          the wordmark beside it.

          It was missing. The name used to arrive with BookSwitch, the bar on
          Book now and Send, so the marketplace tabs and every transporter
          screen wore it. §83 replaced those tabs with the directory, and the
          three new roots -- Carriers, My travel, Account -- were built on
          this bare bar. The app's own name then appeared nowhere a traveller
          could see it: only on the welcome screen, at sign-in, and in the
          browser tab. Someone who opened a bookmark was looking at an
          unnamed application.

          Only on ROOT screens. A pushed screen shows a back chevron and the
          title of where you are, which is what a phone means by depth; the
          name belongs where the journey starts.
        */}
        {!header && !bar && (
          <div className="flex min-h-[52px] items-center justify-between gap-2 px-[21px]">
            <Brand home />
            <span className="flex shrink-0 items-center gap-2"><BarControls /></span>
          </div>
        )}

        {!header && bar && (
          <>
            <div className="flex min-h-[52px] items-center gap-1 px-2">
              {onBack && (
                <button type="button" onClick={onBack} aria-label="Back"
                        className="grid h-11 w-11 place-items-center rounded-full text-accent active:bg-accent-wash">
                  <Icon name="back" className="h-6 w-6" />
                </button>
              )}
              {!large && title && (
                <h1 className="flex-1 truncate px-1 text-center font-display text-[17px] font-bold">
                  {title}
                </h1>
              )}
              {(large || !title) && <div className="flex-1" />}
              <div className="flex items-center gap-2">
                {action}
                <BarControls />
              </div>
            </div>
            {large && (
              <h1 className="px-4 pb-3 pt-1 font-display text-[30px] font-extrabold">{title}</h1>
            )}
          </>
        )}
      </header>
      {/* The tab bar is on every screen now, so every scroller needs its
          room. There is no longer a case that does not. */}
      <div className="scroller tabbar-gap">{children}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ sheet */

/**
 * A bottom sheet.
 *
 * Comes up from the bottom edge because that is where the thumb is, and can
 * be dismissed by dragging it back down -- the gesture people already expect,
 * and the reason this is not a centred modal.
 */
export function Sheet({
  open, onClose, title, children, full = false, closeLabel = 'Close',
  dismissible = true,
}: {
  closeLabel?: string
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  /** Take the whole screen, for a picker or a long form. */
  full?: boolean
  /**
   * False = the X button is the ONLY way out: no backdrop tap, no Escape, no
   * drag. For a sheet where leaving by accident loses something -- the
   * carrier review, where a stray swipe mid-read drops you back to the queue
   * with no idea whether you had decided (§69).
   */
  dismissible?: boolean
}) {
  const [drag, setDrag] = useState(0)
  const start = useRef<number | null>(null)

  // Freeze the page behind the sheet. Without this the list underneath
  // scrolls when the sheet's own content has nowhere left to go.
  useEffect(() => {
    if (!open || !dismissible) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, dismissible])

  useEffect(() => { if (!open) setDrag(0) }, [open])

  if (!open) return null

  // Portalled to the body. `.appbar` has a backdrop-filter, and a
  // backdrop-filter makes its element the containing block for `fixed`
  // descendants -- a sheet opened from the app bar was rendering inside a 52px
  // strip, with its content present and clipped to nothing.
  return createPortal(
    <div className="fixed inset-0 z-50 mx-auto flex max-w-[480px] flex-col justify-end"
         role="dialog" aria-modal="true">
      {dismissible ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute inset-0 animate-fadeIn bg-ink/35"
        />
      ) : (
        /* A div, not a button: the backdrop still dims and still swallows
           taps meant for the page behind, but it is not a way out. */
        <div aria-hidden className="absolute inset-0 animate-fadeIn bg-ink/35" />
      )}
      <div
        className={`relative animate-sheetIn rounded-t-3xl bg-ground shadow-sheet
                    ${full ? 'h-[92%]' : 'max-h-[88%]'} flex flex-col`}
        style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
      >
        {/*
          THE DRAG LIVES ON THE HANDLE, NOT ON THE SHEET.
          
          These three listeners used to sit on the element above, which
          contains the scroller -- so every scroll gesture inside a sheet was
          also a drag gesture. A swipe to read further down translated the
          sheet instead of scrolling it, and past 120px it closed. Long
          sheets were the worst affected, which is why the carrier review
          surfaced it (§69): it is the longest one in the app.
          
          On the handle it does what a handle is for, and the content below
          scrolls like content.
        */}
        <div
          className="grid shrink-0 cursor-grab place-items-center pb-1 pt-2.5"
          onTouchStart={dismissible ? (e) => { start.current = e.touches[0]!.clientY } : undefined}
          onTouchMove={dismissible ? (e) => {
            if (start.current === null) return
            // Downward only: an upward drag on the handle should do nothing
            // rather than lift the sheet off the bottom of the screen.
            setDrag(Math.max(0, e.touches[0]!.clientY - start.current))
          } : undefined}
          onTouchEnd={dismissible ? () => {
            // A short drag springs back; past a third of the way it is a
            // dismissal. Guessing lower than this makes the sheet feel fragile.
            if (drag > 120) onClose()
            setDrag(0)
            start.current = null
          } : undefined}
        >
          <span className="h-1.5 w-10 rounded-full bg-line-2" />
        </div>
        {/* A full-height sheet leaves only a sliver of backdrop, so dragging
            the handle was effectively the only way out -- and nothing on
            screen said so. On a non-dismissible sheet this button IS the only
            way out, so it is drawn solid rather than as a quiet grey disc. */}
        <div className="relative shrink-0">
          {title && (
            <h2 className="px-14 pb-2 pt-1 text-center font-display text-[17px] font-bold">
              {title}
            </h2>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className={`absolute end-3 top-0 grid h-10 w-10 place-items-center rounded-full
                        ${dismissible
                          ? 'bg-board text-ink-2 active:bg-line'
                          : 'bg-accent text-white active:bg-accent-deep'}`}
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </div>
        <div className="scroller px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px))]">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ------------------------------------------------------------- feedback */

export function Spinner({ label }: { label?: string }) {
  const msg = useT()
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-ink-3" role="status">
      <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-line-2 border-t-accent" />
      <span className="text-[13.5px]">{label ?? msg('common.loading')}</span>
    </div>
  )
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = useT()
  const code = (error as { code?: string })?.code
  const raw = error instanceof Error ? error.message : String(error)
  // A message that is itself a catalogue key resolves; anything else is the
  // server's own words, which are better shown than swallowed.
  const fallback = /^[a-z][\w]*\.[\w.]+$/.test(raw) ? msg(raw, { fallback: raw }) : raw
  return (
    <div className="mx-4 my-3 rounded-2xl bg-band-red-wash p-4" role="alert">
      <div className="flex items-start gap-2.5">
        <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-band-red" />
        <p className="text-[14px] text-ink">{code ? msg(`error.${code}`, { fallback }) : fallback}</p>
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-quiet mt-3">
          {msg('common.retry')}
        </button>
      )}
    </div>
  )
}

export function Empty({ title, hint, action }: {
  title: string; hint?: string; action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-8 py-16 text-center">
      <h3 className="font-display text-[17px] font-bold">{title}</h3>
      {hint && <p className="text-[14px] leading-relaxed text-ink-3">{hint}</p>}
      {action && <div className="mt-4 w-full max-w-xs">{action}</div>}
    </div>
  )
}

export function Pill({ tone = 'mute', children }: {
  tone?: 'accent' | 'ok' | 'warn' | 'crit' | 'mute'
  children: ReactNode
}) {
  const tones = {
    accent: 'bg-accent-wash text-accent-deep',
    ok: 'bg-band-green-wash text-band-green',
    warn: 'bg-band-amber-wash text-band-amber',
    crit: 'bg-band-red-wash text-band-red',
    mute: 'bg-board text-ink-3',
  }
  return <span className={`pill ${tones[tone]}`}>{children}</span>
}

/** A tappable row in a grouped list, with the chevron a phone expects. */
export function Row({ title, detail, right, onClick, to, icon }: {
  title: ReactNode
  detail?: ReactNode
  right?: ReactNode
  onClick?: () => void
  to?: string
  icon?: IconName
}) {
  const nav = useNavigate()
  const go = onClick ?? (to ? () => nav(to) : undefined)
  return (
    <button type="button" onClick={go} disabled={!go} className="row disabled:active:bg-transparent">
      {icon && <Icon name={icon} className="h-5 w-5 shrink-0 text-ink-3" />}
      <span className="min-w-0 flex-1 py-3">
        <span className="block truncate text-[15px] font-semibold">{title}</span>
        {detail && <span className="mt-0.5 block truncate text-[13px] text-ink-3">{detail}</span>}
      </span>
      {right}
      {go && <Icon name="chevron" className="h-4 w-4 shrink-0 text-line-2" />}
    </button>
  )
}

/**
 * A footer for a primary action, clear of the home indicator.
 *
 * `pinned` (the default) keeps it in view while the page scrolls. Pass
 * `pinned={false}` where the page is a FORM the person is working down: a
 * button that hovers over the fields being filled in overlaps them and
 * invites a submit before the form is finished.
 */
export function Footer({ children, pinned = true }: {
  children: ReactNode
  pinned?: boolean
}) {
  return <div className={pinned ? 'appfooter' : 'appfooter-end'}>{children}</div>
}
